import React from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Plus, UserPlus, Trash2, X } from "lucide-react";
import { businessApi } from "../../lib/api";

export default function BusinessUsersPage() {
  const { user: me } = useOutletContext();
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [inviting, setInviting] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await businessApi().get("/business/users");
      setItems(data.items || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Kunne ikke hente brugere"); }
    setLoading(false);
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm("Slet denne bruger?")) return;
    try {
      await businessApi().delete(`/business/users/${id}`);
      toast.success("Slettet");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Sletning fejlede"); }
  };

  const isOwner = me?.role === "owner";

  return (
    <div className="space-y-6" data-testid="business-users-page">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-brand-red font-semibold">Brugere</div>
          <h1 className="font-display font-bold text-3xl text-brand-dark mt-1">Portal-brugere</h1>
          <p className="text-sm text-brand-text2 mt-1">Owner-brugere kan invitere kolleger til portalen.</p>
        </div>
        {isOwner && (
          <button onClick={() => setInviting(true)} className="btn-primary ml-auto" data-testid="biz-users-invite">
            <UserPlus size={16}/> Inviter bruger
          </button>
        )}
      </div>

      {loading && <div className="text-brand-text2"><Loader2 className="animate-spin inline mr-2" size={14}/>Indlæser...</div>}

      {!loading && (
        <div className="bg-white border border-brand overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-brand-bg2 text-xs uppercase text-brand-text2">
              <tr>
                <th className="px-4 py-2 text-left">Email</th>
                <th className="px-4 py-2 text-left">Navn</th>
                <th className="px-4 py-2 text-left">Rolle</th>
                <th className="px-4 py-2 text-left">Status</th>
                <th className="px-4 py-2 text-left">Sidste login</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((u) => (
                <tr key={u.id} className="border-t border-gray-100" data-testid={`biz-user-row-${u.id}`}>
                  <td className="px-4 py-2">{u.email}{u.id === me?.id && <span className="text-xs text-brand-text2 ml-2">(dig)</span>}</td>
                  <td className="px-4 py-2">{u.name || "—"}</td>
                  <td className="px-4 py-2"><span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-800">{u.role === "owner" ? "Owner" : "User"}</span></td>
                  <td className="px-4 py-2">
                    <span className={`text-xs px-1.5 py-0.5 ${u.status === "active" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                      {u.status === "active" ? "Aktiv" : u.status === "invited" ? "Inviteret" : u.status}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-xs text-brand-text2">{u.last_login_iso ? new Date(u.last_login_iso).toLocaleString("da-DK") : "Aldrig"}</td>
                  <td className="px-4 py-2 text-right">
                    {isOwner && u.id !== me?.id && (
                      <button onClick={() => del(u.id)} className="text-red-600 hover:text-red-800" title="Slet" data-testid={`biz-user-delete-${u.id}`}>
                        <Trash2 size={14}/>
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-brand-text2">Ingen brugere fundet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {inviting && <InviteModal onClose={() => setInviting(false)} onDone={() => { setInviting(false); load(); }}/>}
    </div>
  );
}

function InviteModal({ onClose, onDone }) {
  const [email, setEmail] = React.useState("");
  const [name, setName] = React.useState("");
  const [role, setRole] = React.useState("user");
  const [busy, setBusy] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await businessApi().post("/business/users", { email, name, role });
      toast.success("Invitation sendt");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Kunne ikke invitere"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center px-4 z-50">
      <div className="bg-white w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-bold text-xl text-brand-dark">Inviter bruger</h2>
          <button onClick={onClose}><X size={20}/></button>
        </div>
        <form onSubmit={submit} className="space-y-3" data-testid="biz-invite-form">
          <div>
            <label className="label-flat">Email *</label>
            <input className="input-flat" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="biz-invite-email"/>
          </div>
          <div>
            <label className="label-flat">Navn</label>
            <input className="input-flat" value={name} onChange={(e) => setName(e.target.value)} data-testid="biz-invite-name"/>
          </div>
          <div>
            <label className="label-flat">Rolle</label>
            <select className="input-flat" value={role} onChange={(e) => setRole(e.target.value)} data-testid="biz-invite-role">
              <option value="user">User (kan oprette pakker, se data)</option>
              <option value="owner">Owner (kan også invitere/slette brugere)</option>
            </select>
          </div>
          <p className="text-xs text-brand-text2">Brugeren modtager en email med link til at vælge kodeord. Linket udløber om 72 timer.</p>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary">Annullér</button>
            <button type="submit" disabled={busy} className="btn-primary ml-auto" data-testid="biz-invite-submit">
              {busy ? <Loader2 className="animate-spin" size={14}/> : "Send invitation"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
